import React from "react";
import { Link } from "react-router-dom";
import {
  Users, FileText, Wallet, Car, Activity, LayoutDashboard, TrendingUp,
  ArrowRight, CheckCircle2
} from "lucide-react";
import PhoneMockup from "../components/site/PhoneMockup";
import { ScreenDashboard } from "../components/site/PhoneScreens";

const features = [
  {
    icon: Users,
    title: "Gestion clients",
    desc: "Centralisez vos contacts, coordonnées, historiques et notes. Retrouvez chaque client en un instant.",
    points: ["Fiches clients complètes", "Historique des échanges", "Recherche instantanée"],
    span: "md:col-span-7",
  },
  {
    icon: FileText,
    title: "Factures",
    desc: "Créez des factures professionnelles en moins d'une minute et suivez chaque paiement.",
    points: ["Modèles personnalisables", "Statut des paiements", "Export PDF"],
    span: "md:col-span-5",
  },
  {
    icon: Wallet,
    title: "Dépenses",
    desc: "Saisissez vos achats pros, catégorisez-les et conservez les justificatifs.",
    points: ["Catégories personnalisées", "Photos de justificatifs", "Récap mensuel"],
    span: "md:col-span-5",
  },
  {
    icon: Car,
    title: "Véhicule",
    desc: "Suivez carburant, entretien et kilométrages pour maîtriser vos frais de déplacement.",
    points: ["Carnet d'entretien", "Conso & coûts", "Indemnités kilométriques"],
    span: "md:col-span-7",
  },
  {
    icon: Activity,
    title: "Statistiques",
    desc: "Graphiques clairs sur votre chiffre d'affaires, marge et évolution dans le temps.",
    points: ["Vues mensuelles & annuelles", "Comparaisons", "Tendances"],
    span: "md:col-span-4",
  },
  {
    icon: LayoutDashboard,
    title: "Tableau de bord",
    desc: "Une vue d'ensemble synthétique de votre activité dès l'ouverture de l'application.",
    points: ["KPIs en un coup d'œil", "Activité récente", "Alertes paiements"],
    span: "md:col-span-4",
  },
  {
    icon: TrendingUp,
    title: "Suivi d'activité",
    desc: "Mesurez votre rentabilité réelle, identifiez vos meilleurs clients et postes de dépenses.",
    points: ["Marge nette", "Top clients", "Postes principaux"],
    span: "md:col-span-4",
  },
];

export default function FeaturesPage() {
  return (
    <main data-testid="page-features" className="relative pt-32 md:pt-40 pb-24">
      <div className="absolute inset-0 bg-grid opacity-30 [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_70%)]" />
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-blue-600/20 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-blue-400">Fonctionnalités</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white">
            Toutes les fonctionnalités <span className="text-gradient-blue">de BoostPro.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-400">
            Une suite complète pensée pour les indépendants, artisans et petites entreprises —
            sans complexité inutile.
          </p>
        </div>

        {/* Bento grid */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className={`group relative ${f.span} rounded-2xl p-7 border border-white/5 bg-gradient-to-br from-slate-900/80 to-slate-900/30 hover:border-blue-400/30 transition-all duration-500 overflow-hidden`}
              >
                <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-blue-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 ring-1 ring-white/10">
                    <Icon size={22} className="text-blue-300" />
                  </div>
                  <h3 className="mt-5 font-display text-xl md:text-2xl font-semibold text-white">{f.title}</h3>
                  <p className="mt-2 text-slate-400 text-sm md:text-base leading-relaxed">{f.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {f.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle2 size={15} className="mt-0.5 text-emerald-400 flex-shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Visual section */}
        <div className="mt-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-400">Expérience</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white tracking-tight">
              Conçue pour aller vite,<br />
              <span className="text-gradient-blue">sans sacrifier la clarté.</span>
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed">
              Chaque écran de BoostPro a été pensé pour vous faire gagner du temps :
              navigation intuitive, raccourcis intelligents, et données toujours à portée de main.
            </p>
            <div className="mt-8">
              <Link
                data-testid="features-cta-contact"
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-medium px-7 py-3.5 glow-blue transition-all"
              >
                Une question ? Contactez-nous <ArrowRight size={18} />
              </Link>
            </div>
          </div>
          <div className="flex justify-center">
            <PhoneMockup floating testid="features-phone-mockup">
              <ScreenDashboard />
            </PhoneMockup>
          </div>
        </div>
      </div>
    </main>
  );
}
